import React, {useEffect, useState} from 'react';
import {Avatar, Button, Card, CardContent, CircularProgress, IconButton, Skeleton, Tooltip} from '@mui/material';
import {AddShoppingCart, Bolt, Check, Delete, Person, SentimentDissatisfied, SentimentSatisfied, SentimentSatisfiedAlt, Star} from '@mui/icons-material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ThreeSixtyIcon from '@mui/icons-material/ThreeSixty';
import {toast} from 'react-toastify';
import api from '../../api/axios';
import {OfferDTO} from '../user/on-sale/types';
import BuyConfirmModal from './modals/BuyConfirmModal';
import ItemScreenshotModal from '../items/ItemScreenshotModal';
import Item3DViewerModal from '../items/Item3DViewerModal';
import {formatMoney, moneyFromMinor} from '../utils/UnitConversion.utils';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from '../../store';
import {addToCartLocally, CartItemDTO, removeItemFromCartLocally} from '../../store/slices/cartSlice';
import {setAuthState, SteamAccount, subtractFromWalletAfterBuy} from '../../store/slices/authSlice';
import {useCurrency} from '../../hooks/useCurrency';
import {RARITY_COLORS, RarityGrade} from '../utils/RarityColors';
import {useNavigate} from 'react-router-dom';
import {formatSteamImageUrl, useSteamImageUrl} from '../../hooks/useSteamImageUrl';
import {getAuthStateFromLocalStorage} from "../utils/LocalStorageAuth";

type Props = {
  offer: OfferDTO;
  onBuySuccess: (offerId: string | number) => void;
};

const PHOTO_ALLOWED_TYPES = [
  'PISTOL', 'SMG', 'RIFLE', 'SNIPER_RIFLE', 'MACHINE_GUN',
  'SHOTGUN', 'EQUIPMENT', 'KNIFE', 'GLOVES'
];

const MarketplaceOfferCard: React.FC<Props> = ({offer: initialOffer, onBuySuccess}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);
  const {convertFromUSD} = useCurrency();
  const auth = useSelector((state: RootState) => state.auth);
  const cartItems = useSelector((state: RootState) => state.cart.items);

  // Use local state for the offer to allow immediate status updates
  const [offer, setOffer] = useState(initialOffer);
  const globalSelectedAccountId = auth.selectedSteamAccount?.id;
  const isAuthenticated = auth.isAuthenticated;

  const item = offer.item;
  const details = item.details;
  const catalog = item.catalog;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [screenshotOpen, setScreenshotOpen] = useState(false);
  const [viewer3DOpen, setViewer3DOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [isAddedToCart, setIsAddedToCart] = useState(false);

  // Track image load state to prevent layout shift and image pop-in
  const [isImgLoaded, setIsImgLoaded] = useState(false);

  const isBought = offer.status === 'BOUGHT';

  // Sync isAddedToCart with Redux cart state
  useEffect(() => {
    const inCart = cartItems.some((cartItem: CartItemDTO) => cartItem.offerId === offer.id);
    setIsAddedToCart(inCart);
  }, [cartItems, offer.id]);

  // Sync with prop changes from parent
  useEffect(() => {
    setOffer(initialOffer);
  }, [initialOffer]);

  // Listen for global buy events (e.g. from NavbarCart checkout)
  useEffect(() => {
    const handleGlobalBuy = (event: any) => {
      const boughtIds = event.detail?.offerIds || [];
      if (boughtIds.includes(offer.id)) {
        setOffer(prev => ({ ...prev, status: 'BOUGHT' }));
      }
    };

    window.addEventListener('offers-bought', handleGlobalBuy);
    return () => window.removeEventListener('offers-bought', handleGlobalBuy);
  }, [offer.id]);

  const getGlobalAccountObject = (): SteamAccount | null => {
    if (!globalSelectedAccountId) return null;
    return {
      id: globalSelectedAccountId,
      steamId: auth.selectedSteamAccount?.steamId ?? '',
      profileName: auth.selectedSteamAccount?.profileName ?? '',
      avatarUrl: auth.selectedSteamAccount?.avatarUrl ?? '',
      profileSteamUrl: '',
    };
  };

  const [buyNowSelectedSteamAccount, setBuyNowSelectedSteamAccount] = useState<SteamAccount | null>(getGlobalAccountObject());

  useEffect(() => {
    if (globalSelectedAccountId) {
      setBuyNowSelectedSteamAccount(getGlobalAccountObject());
    }
  }, [globalSelectedAccountId, auth.selectedSteamAccount?.avatarUrl]);

  const rawImg = catalog.imageLink ?? undefined;
  const img = useSteamImageUrl(rawImg, '360fx360f');
  const rarityKey = typeof details?.rarity === 'string' ? details.rarity : '';
  const rarityColor = RARITY_COLORS[rarityKey as RarityGrade] ?? '#ffffff';

  // Reset image load state when the image URL changes
  useEffect(() => {
    setIsImgLoaded(false);
  }, [img]);

  const floatValue = details?.floatValue ?? null;
  const floatPercent = floatValue === null ? 0 : Math.min(Math.max(floatValue * 100, 0), 100);

  const formatDeliveryTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderBolts = () => {
    if (offer.sellerSuccessfulTradesCount === 0) return null;
    const time = offer.sellerMedianDeliveryTime;
    let activeBolts = 0;
    if (time < 10) activeBolts = 3;
    else if (time < 30) activeBolts = 2;
    else if (time < 120) activeBolts = 1;

    return (
        <Tooltip title={`Median delivery: ${formatDeliveryTime(offer.sellerMedianDeliveryTime)}`} arrow>
          <div className="flex">
            {[1, 2, 3].map((i) => (
                <Bolt
                    key={i}
                    sx={{
                      fontSize: 16,
                      color: i <= activeBolts ? '#3b82f6' : '#374151',
                      marginLeft: -0.5
                    }}
                />
            ))}
          </div>
        </Tooltip>
    );
  };

  const renderSellerTrust = () => {
    if (offer.sellerTradeCount === 0) return null;
    const rate = (offer.sellerSuccessfulTradesCount / offer.sellerTradeCount) * 100;
    const showStar = offer.sellerTradeCount >= 100;

    let SmileyIcon = SentimentDissatisfied;
    let iconColor = '#ef4444';

    if (rate > 92) {
      SmileyIcon = SentimentSatisfiedAlt;
      iconColor = '#22c55e';
    } else if (rate > 80) {
      SmileyIcon = SentimentSatisfied;
      iconColor = '#eab308';
    }

    return (
        <div className="flex items-center gap-0.5 ml-1">
          <Tooltip title={`Success rate: ${rate.toFixed(1)}% (${offer.sellerSuccessfulTradesCount}/${offer.sellerTradeCount} trades)`} arrow>
            <SmileyIcon sx={{ fontSize: 16, color: iconColor }} />
          </Tooltip>
          {showStar && (
              <Tooltip title={`Experienced Seller ${offer.sellerSuccessfulTradesCount} successful trades`} arrow>
                <Star sx={{ fontSize: 16, color: '#eab308' }} />
              </Tooltip>
          )}
        </div>
    );
  };

  const handleInspectIngame = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (details?.ingameLink) {
      window.location.href = details.ingameLink;
    } else {
      toast.info('No inspect link available');
    }
  };

  const handleScreenshot = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setScreenshotOpen(true);
  };

  const handle3DViewer = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setViewer3DOpen(true);
  };

  const handleAddToCartClick = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (cartLoading || loading || isAddedToCart || isBought || offer.isItemOwner) return;
    if (!globalSelectedAccountId) {
      toast.error('No Steam Account selected globally');
      return;
    }
    setCartLoading(true);
    try {
      await api.post('/cart/add', {
        offerId: offer.id,
        priceMinor: offer.priceMinor,
        item: item,
        buyerSelectedSteamAccountId: globalSelectedAccountId
      });
      const cartPayload: CartItemDTO = {
        offerId: offer.id,
        priceMinor: offer.priceMinor,
        item: item as any,
        selectedBuyerSteamAccountId: globalSelectedAccountId
      };
      dispatch(addToCartLocally(cartPayload));
      toast.success('Added to cart');
    } catch (error: any) {
      toast.error(error?.response?.data ?? 'Failed to add to cart');
    } finally {
      setCartLoading(false);
    }
  };

  const handleRemoveFromCartClick = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (cartLoading || loading || !isAddedToCart) return;
    setCartLoading(true);
    try {
      await api.delete(`/cart/${offer.id}`);
      dispatch(removeItemFromCartLocally(offer.id));
      toast.info('Removed from cart');
    } catch (error: any) {
      toast.error(error?.response?.data ?? 'Failed to remove from cart');
    } finally {
      setCartLoading(false);
    }
  };

  const handleBuyNowClick = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (isBought || offer.isItemOwner || loading || cartLoading) return;
    setBuyNowSelectedSteamAccount(getGlobalAccountObject());
    setConfirmOpen(true);
  };

  const handleBuyConfirm = async () => {
    const finalAccountId = buyNowSelectedSteamAccount?.id || globalSelectedAccountId;
    if (!finalAccountId) {
      toast.warn('No Steam Account selected');
      return;
    }
    if (loading) return;
    setLoading(true);
    try {
      await api.post('/buy', {
        offerId: offer.id,
        priceMinor: offer.priceMinor,
        buyerSelectedSteamAccountId: finalAccountId
      });
      dispatch(subtractFromWalletAfterBuy(offer.priceMinor));
      dispatch(removeItemFromCartLocally(offer.id));

      setOffer(prev => ({ ...prev, status: 'BOUGHT' }));

      const syncRes = await api.get('/sync');
      dispatch(setAuthState(getAuthStateFromLocalStorage(syncRes.data)));

      setConfirmOpen(false);
      toast.success('Purchase successful!');
      onBuySuccess(offer.id);
    } catch (error: any) {
      toast.error(error?.response?.data ?? 'Buy failed');
    } finally {
      setLoading(false);
    }
  };

  const renderAttachments = () => {
    const attachments = item.attachments || [];
    if (attachments.length === 0) return null;
    return (
        <div className="absolute bottom-1 left-1 flex flex-col-reverse gap-0 z-20 backdrop-blur-[1px] rounded-md">
          {attachments.map((att, idx) => (
              <div key={att.id || idx} className="-mb-1 first:mb-0">
                <Tooltip title={att.marketHashName} arrow placement="right">
                  <div className="relative group/sticker">
                    <img
                        src={formatSteamImageUrl(att.imageLink, '100fx100f')}
                        alt={att.marketHashName || 'attachment'}
                        className="w-7 h-7 object-contain transition-transform duration-200 group-hover/sticker:scale-[1.8] group-hover/sticker:translate-x-1 drop-shadow-md"
                    />
                  </div>
                </Tooltip>
              </div>
          ))}
        </div>
    );
  };

  return (
      <>
        <Card className={`relative bg-[#1c1f26] border border-gray-700 transition-opacity duration-300 ${isBought ? 'opacity-50 grayscale-[0.3]' : 'opacity-100'}`}>
          <CardContent className="flex flex-col !p-3">
            <div className="h-[36px]">
              <div className="text-white font-semibold text-[0.80rem] leading-[1.1] line-clamp-2">
                {item.catalog.groupName ?? item.catalog.marketHashName ?? 'Unknown Item'}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                {item.catalog.isSouvenir && <span className="text-yellow-400 font-semibold text-[0.7rem] tracking-[0.3px]">Souvenir</span>}
                {item.catalog.isStattrak && <span className="text-orange-400 font-semibold text-[0.7rem] tracking-[0.3px]">StatTrak™</span>}
                {catalog?.wearName && (
                    <span className="text-gray-300 text-[0.7rem] font-medium tracking-[0.2px] capitalize">
                  {catalog.wearName.replace('_', ' ').toLowerCase()}
                      {details?.phase && <> ({details?.phase})</>}
                </span>
                )}
              </div>
            </div>

            <div
                className="relative flex justify-center items-end mb-3 rounded-md overflow-hidden group/img cursor-pointer h-[140px] lg:h-[160px]"
                onClick={() => navigate(`/market/offer/${offer.id}`)}
                style={{
                  background: `linear-gradient(to top, var(--rarity-gradient-color, ${rarityColor}99) 0%, transparent 100%)`,
                }}
            >
              <div className={`hidden [--rarity-gradient-color:${rarityColor}33]`} />

              <style dangerouslySetInnerHTML={{ __html: `
                .rarity-bg-${offer.id} {
                  background: linear-gradient(to top, ${rarityColor}99 0%, transparent 100%);
                }
              `}} />

              <div className={`absolute inset-0 rarity-bg-${offer.id}`} />

              {img ? (
                  <>
                    {!isImgLoaded && (
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height="100%"
                            animation="wave"
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                        />
                    )}
                    <img
                        src={img}
                        alt={item.catalog.marketHashName ?? 'item'}
                        onLoad={() => setIsImgLoaded(true)}
                        className={`w-44 h-44 object-contain z-10 ${
                            isImgLoaded ? 'opacity-100' : 'opacity-0 absolute'
                        }`}
                        style={{ objectPosition: 'center 90%' }}
                    />
                  </>
              ) : (
                  <Avatar sx={{ width: 80, height: 80, zIndex: 10 }} />
              )}
              {renderAttachments()}
              <div className="absolute bottom-2 right-2 flex flex-col gap-1.5 z-20">
                <Tooltip title="Inspect In-Game" arrow placement="left">
                  <IconButton size="small" onClick={handleInspectIngame} className="!bg-neutral-800 hover:!bg-neutral-700 !text-white !rounded-full border border-neutral-700/50 !opacity-60 hover:!opacity-100 transition-opacity">
                    <ZoomInIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                {item.catalog.itemGroup && PHOTO_ALLOWED_TYPES.includes(item.catalog.itemGroup) && (
                    <>
                      <Tooltip title="View Screenshot" arrow placement="left">
                        <IconButton size="small" onClick={handleScreenshot} className="!bg-neutral-800 hover:!bg-neutral-700 !text-white !rounded-full border border-neutral-700/50 !opacity-60 hover:!opacity-100 transition-opacity">
                          <CameraAltIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="360° 3D Viewer" arrow placement="left">
                        <IconButton size="small" onClick={handle3DViewer} className="!bg-neutral-800 hover:!bg-neutral-700 !text-white !rounded-full border border-neutral-700/50 !opacity-60 hover:!opacity-100 transition-opacity">
                          <ThreeSixtyIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-0 mb-1">
              <div className="flex items-center gap-1.5">
                <div className="relative flex items-center justify-center w-2 h-2">
                  <span className={`absolute inline-flex h-3 w-3 rounded-full ${offer.sellerIsOnline ? 'bg-green-500/20' : 'bg-neutral-800'}`}></span>
                  <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${offer.sellerIsOnline ? 'bg-green-400' : 'bg-neutral-400'}`}></span>
                </div>
                <Tooltip title={!offer.sellerIsOnline ? `Last seen: ${formatDate(offer.sellerLastSeen)}` : ''} arrow placement="top">
                <span className={`text-[9px] font-black uppercase tracking-wider text-neutral-500 ${!offer.sellerIsOnline ? 'cursor-help' : ''}`}>
                  {offer.sellerIsOnline ? 'Online' : 'Offline'}
                </span>
                </Tooltip>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-white font-semibold flex gap-1">
                  <div className="text-gray-400 font-medium">{currencySymbol}</div>
                  <div>{formatMoney(convertFromUSD(moneyFromMinor(offer.priceMinor)))}</div>
                </div>
                <div className="flex items-center">
                  {renderBolts()}
                  {renderSellerTrust()}
                </div>
              </div>
            </div>

            <div className="h-[40px] w-full">
              {floatValue !== null && (
                  <>
                    <div className="relative h-[8px] rounded-full w-[70%]" style={{ background: 'linear-gradient(to right, #14532d 0%, #14532d 7%, #22c55e 7%, #22c55e 15%, #eab308 15%, #eab308 38%, #f87171 38%, #f87171 45%, #b91c1c 45%, #b91c1c 100%)' }}>
                      <div className="absolute top-1/2 -translate-y-1/2 w-[2px] h-[12px] bg-white rounded-sm shadow" style={{ left: `${floatPercent}%` }} />
                    </div>
                    <div className="flex justify-between text-[0.72rem] text-gray-400 mt-1.5">
                      <div>{floatValue.toFixed(10)}</div>
                      <div>{details?.paintSeed !== undefined && details?.paintSeed !== null ? `#${details.paintSeed}` : ''}</div>
                    </div>
                  </>
              )}
            </div>

            <div className="flex items-center gap-2 mt-auto">
              {!isAuthenticated ? (
                  <Button fullWidth variant="contained" disabled sx={{ height: 40 }}>Login to buy</Button>
              ) : isBought ? (
                  <Button fullWidth variant="contained" disabled sx={{ height: 40, '&.Mui-disabled': { backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' } }}>
                    <Check fontSize="small" sx={{ mr: 1 }} /> Bought
                  </Button>
              ) : offer.isItemOwner ? (
                  <Button fullWidth variant="contained" disabled startIcon={<Person fontSize="small" />} sx={{ height: 40 }}>Your Offer</Button>
              ) : (
                  <>
                    <Button
                        variant="contained"
                        className="group"
                        disabled={cartLoading || loading}
                        onClick={isAddedToCart ? handleRemoveFromCartClick : handleAddToCartClick}
                        sx={{ minWidth: 44, width: 44, height: 40, backgroundColor: isAddedToCart ? '#383838' : undefined }}
                    >
                      {cartLoading ? (
                          <CircularProgress size={20} color="inherit" />
                      ) : isAddedToCart ? (
                          <div className="relative w-5 h-5 flex items-center justify-center">
                            <Check fontSize="small" className="absolute transition-opacity duration-200 opacity-100 group-hover:opacity-0" />
                            <Delete fontSize="small" className="absolute transition-opacity duration-200 opacity-0 group-hover:opacity-100 text-white" />
                          </div>
                      ) : (
                          <AddShoppingCart fontSize="small" />
                      )}
                    </Button>
                    <Button fullWidth variant="contained" sx={{ height: 40 }} disabled={loading || cartLoading} onClick={handleBuyNowClick}>
                      {loading ? <CircularProgress size={24} color="inherit" /> : 'Buy Now'}
                    </Button>
                  </>
              )}
            </div>
          </CardContent>
        </Card>

        {confirmOpen && (
            <BuyConfirmModal
                open={confirmOpen}
                loading={loading}
                offer={offer}
                selectedSteamAccount={buyNowSelectedSteamAccount}
                setSelectedSteamAccount={setBuyNowSelectedSteamAccount}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleBuyConfirm}
            />
        )}

        <ItemScreenshotModal
            open={screenshotOpen}
            assetId={item.itemAssetId}
            onClose={() => setScreenshotOpen(false)}
        />

        <Item3DViewerModal
            open={viewer3DOpen}
            assetId={item.itemAssetId}
            onClose={() => setViewer3DOpen(false)}
        />
      </>
  );
};

export default MarketplaceOfferCard;
